import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { RestrictedPermission } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';

type PermissionRestrictedRouteProps = Readonly<{
  children: ReactElement;
  requiredPermissions: RestrictedPermission[];
}>;

// The sandbox blocks navigation to HOME, and a blocked navigation never changes the URL — so
// falling back there would leave the visitor on a page they cannot see and cannot leave.
const FALLBACK_ROUTE = IS_SANDBOX ? APP_ROUTE.COACHING_DASHBOARD : APP_ROUTE.HOME;

export const PermissionRestrictedRoute = ({
  children,
  requiredPermissions,
}: PermissionRestrictedRouteProps) => {
  const { data: userPermissions, isError } = useFetchUserPermissionsQuery(undefined);

  if (isError) {
    return <Navigate to={FALLBACK_ROUTE} replace />;
  }

  if (!userPermissions) {
    return null;
  }

  const hasRequiredPermissions = requiredPermissions.every(
    (permission) => userPermissions[permission]?.allowed === true,
  );

  if (!hasRequiredPermissions) {
    return <Navigate to={FALLBACK_ROUTE} replace />;
  }

  return children;
};
